import { useEffect, useRef, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { endOfDay } from 'date-fns';

import { Messages } from 'primereact/messages';
import { ProgressSpinner } from 'primereact/progressspinner';

import ReportingService from '../services/reportingService';
import { AppPage } from '../components/general';
import { LineChartWidget, PieChartWidget, StackedBarWidget } from '../components/widget';
import { ChartSeries, PieSeries, BarData } from '../models/chart/chart';
import { CreditSummary, FeesSummary, VoxSummary, AuwSummary } from '../models/reporting/reporting';
import CalendarRange, { DefaultRangeSelectType } from '../components/common/CalendarRange';
import SummaryCard from '../components/dashboard/SummaryCard';
import DashboardDetailDialog, { DetailType } from '../components/dashboard/DashboardDetailDialog';

const Dashboard = (): JSX.Element => {
  const errors = useRef<Messages>(null);
  const navigate = useNavigate();

  const [creditLoading, setCreditLoading] = useState(true);
  const [feesLoading, setFeesLoading] = useState(true);
  const [voxLoading, setVoxLoading] = useState(true);
  const [auwLoading, setAuwLoading] = useState(true);

  const [credit, setCredit] = useState<CreditSummary | null>(null);
  const [fees, setFees] = useState<FeesSummary | null>(null);
  const [vox, setVox] = useState<VoxSummary | null>(null);
  const [auw, setAuw] = useState<AuwSummary | null>(null);

  const now = new Date();
  const start = new Date(now.getFullYear(), now.getMonth(), 1);
  const end = endOfDay(new Date(now.getFullYear(), now.getMonth() + 1, 0));
  const [dateRange, setDateRange] = useState<Date[] | undefined>([start, end]);

  const [detailVisible, setDetailVisible] = useState(false);
  const [detailType, setDetailType] = useState<DetailType>('credit');
  const [detailVendor, setDetailVendor] = useState<string | undefined>();
  const [detailStatus, setDetailStatus] = useState<string | undefined>();

  const reportingService = ReportingService.getInstance();

  const showError = (error: string) => {
    errors.current?.show({ severity: 'error', summary: 'Error:', detail: error, life: 10000 });
  };

  const fetchAll = useCallback(
    (startDate: Date, endDate: Date) => {
      setFeesLoading(true);
      setCreditLoading(true);
      setVoxLoading(true);
      setAuwLoading(true);

      reportingService
        .getFeesSummary(startDate, endDate)
        .then((data) => setFees(data))
        .catch((err: Error) => showError(err.message))
        .finally(() => setFeesLoading(false));

      reportingService
        .getCreditSummary(startDate, endDate)
        .then((data) => setCredit(data))
        .catch((err: Error) => showError(err.message))
        .finally(() => setCreditLoading(false));

      reportingService
        .getVoxSummary(startDate, endDate)
        .then((data) => setVox(data))
        .catch((err: Error) => showError(err.message))
        .finally(() => setVoxLoading(false));

      reportingService
        .getAuwSummary(startDate, endDate)
        .then((data) => setAuw(data))
        .catch((err: Error) => showError(err.message))
        .finally(() => setAuwLoading(false));
    },
    [reportingService]
  );

  useEffect(() => {
    fetchAll(start, end);
  }, []);

  const openDetail = (type: DetailType, vendor?: string, status?: string) => {
    setDetailType(type);
    setDetailVendor(vendor);
    setDetailStatus(status);
    setDetailVisible(true);
  };

  const buildCombinedVolumeChart = (): ChartSeries[] | undefined => {
    const feesDays = fees?.dailyCounts;
    const creditDays = credit?.dailyCounts;
    const voxDays = vox?.dailyCounts;
    const auwDays = auw?.dailyCounts;
    if (!feesDays && !creditDays && !voxDays && !auwDays) return undefined;

    const series: ChartSeries[] = [];
    if (feesDays && feesDays.length > 0) {
      series.push({ name: 'Fees', data: feesDays.map((d) => ({ x: new Date(d.date).getTime(), y: d.count })) });
    }
    if (creditDays && creditDays.length > 0) {
      series.push({ name: 'Credit', data: creditDays.map((d) => ({ x: new Date(d.date).getTime(), y: d.count })) });
    }
    if (voxDays && voxDays.length > 0) {
      series.push({ name: 'VoX', data: voxDays.map((d) => ({ x: new Date(d.date).getTime(), y: d.count })) });
    }
    if (auwDays && auwDays.length > 0) {
      series.push({ name: 'AUW', data: auwDays.map((d) => ({ x: new Date(d.date).getTime(), y: d.count })) });
    }
    return series.length > 0 ? series : undefined;
  };

  const buildCombinedSuccessFailChart = (): ChartSeries[] | undefined => {
    const allDays = [
      ...(fees?.dailyCounts ?? []),
      ...(credit?.dailyCounts ?? []),
      ...(vox?.dailyCounts ?? []),
      ...(auw?.dailyCounts ?? []),
    ];
    if (allDays.length === 0) return undefined;

    const dateMap = new Map<number, { success: number; failed: number }>();
    allDays.forEach((d) => {
      const ts = new Date(d.date).getTime();
      const existing = dateMap.get(ts) ?? { success: 0, failed: 0 };
      existing.success += d.successCount;
      existing.failed += d.failedCount;
      dateMap.set(ts, existing);
    });

    const sorted = Array.from(dateMap.entries()).sort((a, b) => a[0] - b[0]);
    return [
      { name: 'Success', data: sorted.map(([ts, v]) => ({ x: ts, y: v.success })) },
      { name: 'Failed', data: sorted.map(([ts, v]) => ({ x: ts, y: v.failed })) },
    ];
  };

  const buildSuccessRateBar = (): BarData | undefined => {
    const categories: string[] = [];
    const successData: number[] = [];
    const failedData: number[] = [];

    if (fees) {
      categories.push('Fees');
      successData.push(fees.successCount);
      failedData.push(fees.failedCount);
    }
    if (credit) {
      categories.push('Credit');
      successData.push(credit.successCount);
      failedData.push(credit.failedCount);
    }
    if (vox) {
      categories.push('VoX');
      successData.push(vox.successCount);
      failedData.push(vox.failedCount);
    }
    if (auw) {
      categories.push('AUW');
      successData.push(auw.successCount);
      failedData.push(auw.failedCount);
    }

    if (categories.length === 0) return undefined;
    return {
      categories,
      series: [
        { name: 'Success', data: successData },
        { name: 'Failed', data: failedData },
      ],
    };
  };

  const buildCombinedStatusPie = (): PieSeries | undefined => {
    const statusMap = new Map<string, number>();
    [fees?.statusBreakdown, credit?.statusBreakdown, vox?.statusBreakdown, auw?.statusBreakdown].forEach((breakdown) => {
      breakdown?.forEach((s) => {
        statusMap.set(s.status, (statusMap.get(s.status) ?? 0) + s.count);
      });
    });
    if (statusMap.size === 0) return undefined;
    return {
      labels: Array.from(statusMap.keys()),
      data: Array.from(statusMap.values()),
    };
  };

  const formatDuration = (ms: number): string => {
    if (ms < 1000) return `${Math.round(ms)} ms`;
    return `${(ms / 1000).toFixed(1)} s`;
  };

  const allLoading = feesLoading || creditLoading || voxLoading || auwLoading;
  const anyLoaded = fees || credit || vox || auw;


  const totalRequests = (fees?.totalRequests ?? 0) + (credit?.totalRequests ?? 0) + (vox?.totalOrders ?? 0) + (auw?.totalOrders ?? 0);
  const totalSuccess = (fees?.successCount ?? 0) + (credit?.successCount ?? 0) + (vox?.successCount ?? 0) + (auw?.successCount ?? 0);
  const totalFailed = (fees?.failedCount ?? 0) + (credit?.failedCount ?? 0) + (vox?.failedCount ?? 0) + (auw?.failedCount ?? 0);
  const overallSuccessRate = totalRequests > 0 ? Math.round((totalSuccess / totalRequests) * 1000) / 10 : 0;
  const totalVendors =
    (fees?.vendorBreakdown?.length ?? 0) +
    (credit?.vendorBreakdown?.length ?? 0) +
    (vox?.vendorBreakdown?.length ?? 0) +
    (auw?.vendorBreakdown?.length ?? 0);

  const avgDuration = (() => {
    let sum = 0;
    let count = 0;
    if (fees && fees.totalRequests > 0) { sum += fees.avgDurationMs * fees.totalRequests; count += fees.totalRequests; }
    if (credit && credit.totalRequests > 0) { sum += credit.avgDurationMs * credit.totalRequests; count += credit.totalRequests; }
    return count > 0 ? sum / count : 0;
  })();

  const renderSpinner = () => (
    <div className="flex justify-center items-center py-20">
      <ProgressSpinner style={{ width: '60px', height: '60px' }} />
    </div>
  );

  return (
    <AppPage
      title="Dashboard"
      breadcrumbs="Operations Overview"
      headerEnd={
        <CalendarRange
          rangeSelectOptions={[
            DefaultRangeSelectType.PastWeek,
            DefaultRangeSelectType.Past2Weeks,
            DefaultRangeSelectType.Past30Days,
            DefaultRangeSelectType.CurrentMonth,
          ]}
          value={dateRange}
          onValueChange={(value) => {
            if (!!value && value.length === 2) {
              fetchAll(value[0], value[1]);
            }
            setDateRange(value);
          }}
        />
      }
    >
      <Messages ref={errors} />

      {allLoading ? (
        renderSpinner()
      ) : (
        <div className="flex flex-col gap-6">

          {/* ═══════════ Hero KPIs ═══════════ */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(6, 1fr)', gap: '1rem', width: '100%' }}>
            <SummaryCard
              title="Total Transactions"
              value={totalRequests.toLocaleString()}
              icon="pi pi-chart-bar"
              iconColor="bg-[#0a1f3d]"
            />
            <SummaryCard
              title="Successful"
              value={totalSuccess.toLocaleString()}
              icon="pi pi-check-circle"
              iconColor="bg-[#76b729]"
            />
            <SummaryCard
              title="Failed"
              value={totalFailed.toLocaleString()}
              icon="pi pi-times-circle"
              iconColor="bg-[#e4677e]"
            />
            <SummaryCard
              title="Success Rate"
              value={`${overallSuccessRate}%`}
              icon="pi pi-percentage"
              iconColor="bg-[#2571ab]"
            />
            <SummaryCard
              title="Avg Duration"
              value={formatDuration(avgDuration)}
              icon="pi pi-clock"
              iconColor="bg-[#0a1f3d]"
            />
            <SummaryCard
              title="Active Vendors"
              value={totalVendors}
              icon="pi pi-sitemap"
              iconColor="bg-[#2571ab]"
            />
          </div>

          {/* ═══════════ Combined Volume Chart (full width) ═══════════ */}
          {anyLoaded && (
            <div style={{ width: '100%', minWidth: 0 }}>
              <LineChartWidget title="Transaction Volume by Product" chartSeries={buildCombinedVolumeChart()} chartHeight="350px" />
            </div>
          )}

          {/* ═══════════ Success/Fail Trend + Status Pie + Product Comparison ═══════════ */}
          {anyLoaded && (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '1.25rem', width: '100%' }}>
              <div style={{ minWidth: 0 }}>
                <LineChartWidget title="Success vs Failed (All Products)" chartSeries={buildCombinedSuccessFailChart()} chartHeight="320px" />
              </div>
              <div style={{ minWidth: 0 }}>
                <PieChartWidget title="Overall Status Breakdown" pieSeries={buildCombinedStatusPie()} chartHeight="320px" />
              </div>
              <div style={{ minWidth: 0 }}>
                <StackedBarWidget title="Success vs Failed by Product" barData={buildSuccessRateBar()} chartHeight="320px" />
              </div>
            </div>
          )}

          {/* ═══════════ Per-Product Summary Cards ═══════════ */}
          <div>
            <h2 className="text-[1rem] font-semibold text-gray-500 uppercase tracking-wide mb-4">Product Breakdown</h2>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: '1rem', width: '100%' }}>
              {/* Fees */}
              <div
                className="rounded-xl bg-white border border-gray-100 shadow-sm p-5 hover:shadow-md transition-all cursor-pointer"
                onClick={() => navigate('/products/fees')}
              >
                <div className="flex items-center gap-3 mb-3">
                  <div className="rounded-lg flex items-center justify-center text-white text-sm bg-[#2571ab] flex-shrink-0" style={{ width: '36px', height: '36px' }}>
                    <i className="pi pi-dollar" />
                  </div>
                  <span className="font-semibold text-gray-800">Fees</span>
                </div>
                <div className="grid grid-cols-2 gap-y-2 gap-x-4 text-sm">
                  <div className="text-gray-400">Requests</div>
                  <div className="font-semibold text-gray-800 text-right">{fees?.totalRequests?.toLocaleString() ?? '0'}</div>
                  <div className="text-gray-400">Success Rate</div>
                  <div className="font-semibold text-gray-800 text-right">{fees?.successRate ?? 0}%</div>
                  <div className="text-gray-400">Avg Duration</div>
                  <div className="font-semibold text-gray-800 text-right">{formatDuration(fees?.avgDurationMs ?? 0)}</div>
                  <div className="text-gray-400">Vendors</div>
                  <div className="font-semibold text-gray-800 text-right">{fees?.vendorBreakdown?.length ?? 0}</div>
                </div>
              </div>

              {/* Credit */}
              <div
                className="rounded-xl bg-white border border-gray-100 shadow-sm p-5 hover:shadow-md transition-all cursor-pointer"
                onClick={() => navigate('/products/credit')}
              >
                <div className="flex items-center gap-3 mb-3">
                  <div className="rounded-lg flex items-center justify-center text-white text-sm bg-[#0a1f3d] flex-shrink-0" style={{ width: '36px', height: '36px' }}>
                    <i className="pi pi-credit-card" />
                  </div>
                  <span className="font-semibold text-gray-800">Credit</span>
                </div>
                <div className="grid grid-cols-2 gap-y-2 gap-x-4 text-sm">
                  <div className="text-gray-400">Requests</div>
                  <div className="font-semibold text-gray-800 text-right">{credit?.totalRequests?.toLocaleString() ?? '0'}</div>
                  <div className="text-gray-400">Success Rate</div>
                  <div className="font-semibold text-gray-800 text-right">{credit?.successRate ?? 0}%</div>
                  <div className="text-gray-400">Avg Duration</div>
                  <div className="font-semibold text-gray-800 text-right">{formatDuration(credit?.avgDurationMs ?? 0)}</div>
                  <div className="text-gray-400">Vendors</div>
                  <div className="font-semibold text-gray-800 text-right">{credit?.vendorBreakdown?.length ?? 0}</div>
                </div>
              </div>

              {/* VoX */}
              <div
                className="rounded-xl bg-white border border-gray-100 shadow-sm p-5 hover:shadow-md transition-all cursor-pointer"
                onClick={() => navigate('/products/vox')}
              >
                <div className="flex items-center gap-3 mb-3">
                  <div className="rounded-lg flex items-center justify-center text-white text-sm bg-[#76b729] flex-shrink-0" style={{ width: '36px', height: '36px' }}>
                    <i className="pi pi-bolt" />
                  </div>
                  <span className="font-semibold text-gray-800">VoX</span>
                </div>
                <div className="grid grid-cols-2 gap-y-2 gap-x-4 text-sm">
                  <div className="text-gray-400">Orders</div>
                  <div className="font-semibold text-gray-800 text-right">{vox?.totalOrders?.toLocaleString() ?? '0'}</div>
                  <div className="text-gray-400">Success Rate</div>
                  <div className="font-semibold text-gray-800 text-right">{vox?.successRate ?? 0}%</div>
                  <div className="text-gray-400">Pending</div>
                  <div className="font-semibold text-gray-800 text-right">{vox?.pendingCount?.toLocaleString() ?? '0'}</div>
                  <div className="text-gray-400">Vendors</div>
                  <div className="font-semibold text-gray-800 text-right">{vox?.vendorBreakdown?.length ?? 0}</div>
                </div>
              </div>

              {/* AUW */}
              <div
                className="rounded-xl bg-white border border-gray-100 shadow-sm p-5 hover:shadow-md transition-all cursor-pointer"
                onClick={() => navigate('/products/auw')}
              >
                <div className="flex items-center gap-3 mb-3">
                  <div className="rounded-lg flex items-center justify-center text-white text-sm bg-[#0a1f3d] flex-shrink-0" style={{ width: '36px', height: '36px' }}>
                    <i className="pi pi-shield" />
                  </div>
                  <span className="font-semibold text-gray-800">Underwrite</span>
                </div>
                <div className="grid grid-cols-2 gap-y-2 gap-x-4 text-sm">
                  <div className="text-gray-400">Orders</div>
                  <div className="font-semibold text-gray-800 text-right">{auw?.totalOrders?.toLocaleString() ?? '0'}</div>
                  <div className="text-gray-400">Success Rate</div>
                  <div className="font-semibold text-gray-800 text-right">{auw?.successRate ?? 0}%</div>
                  <div className="text-gray-400">Pending</div>
                  <div className="font-semibold text-gray-800 text-right">{auw?.pendingCount?.toLocaleString() ?? '0'}</div>
                  <div className="text-gray-400">Vendors</div>
                  <div className="font-semibold text-gray-800 text-right">{auw?.vendorBreakdown?.length ?? 0}</div>
                </div>
              </div>

              {/* Price */}
              <div
                className="rounded-xl bg-white border border-gray-100 shadow-sm p-5 hover:shadow-md transition-all cursor-pointer"
                onClick={() => navigate('/products/price')}
              >
                <div className="flex items-center gap-3 mb-3">
                  <div className="rounded-lg flex items-center justify-center text-white text-sm bg-[#6366f1] flex-shrink-0" style={{ width: '36px', height: '36px' }}>
                    <i className="pi pi-tag" />
                  </div>
                  <span className="font-semibold text-gray-800">Price</span>
                </div>
                <div className="flex items-center justify-center h-12 text-[0.8rem] text-gray-400">Coming soon</div>
              </div>
            </div>
          </div>
        </div>
      )}

      <DashboardDetailDialog
        visible={detailVisible}
        onHide={() => setDetailVisible(false)}
        type={detailType}
        startDate={dateRange?.[0] ?? start}
        endDate={dateRange?.[1] ?? end}
        vendor={detailVendor}
        status={detailStatus}
      />
    </AppPage>
  );
};

export default Dashboard;
